/**
 * @theme/component — base class for every interactive custom element in the theme.
 *
 * Provides:
 *   - Ref hydration: ref="name" → this.refs.name. ref="items[]" → array.
 *   - Required-ref validation (static requiredRefs = ['x']).
 *   - Declarative event binding: on:click="/method" or on:click="/method?key=value".
 *   - Lifecycle hooks: connectedCallback, updatedCallback (post Section Renderer), disconnectedCallback.
 *   - Auto-cleanup subscribe(eventName, handler, target?).
 *   - Effect tracking via this.effect(fn) — disposed on disconnect.
 *
 * Patterned on Shopify Horizon v3.4.0. Zero dependencies, vanilla ES2020.
 */

const REF_ATTR = 'ref';
const ON_PREFIX = 'on:';

/* Events that delegate cleanly via global capture-phase listener. Add as needed. */
const DELEGATED_EVENTS = Object.freeze([
  'click', 'change', 'input', 'submit', 'focus', 'blur', 'keydown', 'keyup',
  'pointerenter', 'pointerleave', 'pointerdown', 'pointerup',
  'toggle', 'select', 'dblclick', 'contextmenu',
]);

/** @type {WeakSet<Document>} — installed once per document */
const __delegatedDocs = new WeakSet();

/**
 * Install one capture-phase listener per event type on the document. The handler walks the path from
 * event.target to find any element with a matching `on:<event>="/method"` attribute that lives inside
 * a Component subclass; if found, invokes the method on the enclosing Component.
 */
function installDelegation(doc = document) {
  if (__delegatedDocs.has(doc)) return;
  __delegatedDocs.add(doc);

  for (const eventName of DELEGATED_EVENTS) {
    doc.addEventListener(eventName, (event) => {
      const path = event.composedPath();
      const attrName = `${ON_PREFIX}${eventName}`;
      for (const el of path) {
        if (!(el instanceof Element)) continue;
        const value = el.getAttribute && el.getAttribute(attrName);
        if (!value) continue;
        const host = findEnclosingComponent(el);
        if (!host) continue;
        invokeHandler(host, value, event, el);
        // Don't break — multiple components on the path may react.
      }
    }, { capture: true });
  }
}

function findEnclosingComponent(start) {
  let el = start;
  while (el) {
    if (el instanceof Component) return el;
    el = el.parentNode instanceof ShadowRoot ? el.parentNode.host : el.parentElement;
  }
  return null;
}

function invokeHandler(host, declaration, event, originatingEl) {
  // declaration: "/method" or "/method?key=value&other=2"
  const trimmed = String(declaration).replace(/^\//, '');
  const [methodName, query] = trimmed.split('?');
  if (!methodName) return;
  const params = query ? Object.fromEntries(new URLSearchParams(query)) : {};
  const fn = host[methodName];
  if (typeof fn !== 'function') {
    console.warn(`[Component] ${host.tagName.toLowerCase()}: handler "${methodName}" not found.`);
    return;
  }
  // Annotate event so handlers can read which element triggered without crawling the path.
  Object.defineProperty(event, 'currentBinding', { value: { element: originatingEl, params }, configurable: true });
  try {
    fn.call(host, event, params);
  } catch (err) {
    console.error(`[Component] ${host.tagName.toLowerCase()}.${methodName} threw:`, err);
  }
}

export class MissingRefError extends Error {
  constructor(componentName, refName) {
    super(`<${componentName}>: required ref "${refName}" not found.`);
    this.name = 'MissingRefError';
  }
}

/**
 * The base class.
 *
 * @template {Record<string, HTMLElement | HTMLElement[]>} [Refs=Record<string, HTMLElement | HTMLElement[]>]
 */
export class Component extends HTMLElement {
  /** @type {string[]} — names of refs that must exist when connected. */
  static requiredRefs = [];

  /**
   * If true, defers refresh until the element is in the viewport (set by subclasses that benefit).
   * Default: hydrate immediately on connectedCallback.
   */
  static lazy = false;

  /** @type {Refs} */
  refs = /** @type {Refs} */ ({});

  #subscriptions = new Set();
  #effects = new Set();
  #refObserver = null;
  #connected = false;

  constructor() {
    super();
    installDelegation(this.ownerDocument || document);
  }

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;
    this.#hydrateRefs();
    this.#observeRefs();
    this.#validateRequiredRefs();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#refObserver?.disconnect();
    this.#refObserver = null;
    for (const unsub of this.#subscriptions) unsub();
    this.#subscriptions.clear();
    for (const dispose of this.#effects) dispose();
    this.#effects.clear();
  }

  /**
   * Called after a Section Renderer morph applies new HTML inside the component. Subclasses can override
   * to reconcile state (e.g., re-bind a slider position). Refs are re-hydrated automatically.
   */
  updatedCallback() {}

  /**
   * Subscribe to a custom event with auto-cleanup on disconnect.
   * @param {string} eventName
   * @param {(e: Event) => void} handler
   * @param {EventTarget} [target=document]
   * @returns {() => void} unsubscribe
   */
  subscribe(eventName, handler, target = document) {
    target.addEventListener(eventName, handler);
    const unsub = () => target.removeEventListener(eventName, handler);
    this.#subscriptions.add(unsub);
    return unsub;
  }

  /**
   * Track a reactive effect (from @theme/signals) so it disposes on disconnect.
   * @param {() => void} fn
   * @returns {() => void} dispose
   */
  effect(fn) {
    // Lazy-resolve to avoid a hard dependency on signals.js for components that don't use it.
    let dispose = () => {};
    import('@theme/signals').then((m) => { dispose = m.effect(fn); this.#effects.add(dispose); });
    return () => { dispose(); };
  }

  /**
   * Walk ancestors (across Shadow DOM boundaries) looking for an enclosing Component subclass.
   * @template {typeof Component} T
   * @param {T} ctor
   * @returns {InstanceType<T> | null}
   */
  closestComponent(ctor) {
    let el = this.parentNode instanceof ShadowRoot ? this.parentNode.host : this.parentElement;
    while (el) {
      if (el instanceof ctor) return /** @type {InstanceType<T>} */ (el);
      el = el.parentNode instanceof ShadowRoot ? el.parentNode.host : el.parentElement;
    }
    return null;
  }

  // -- internals -------------------------------------------------------------

  #hydrateRefs() {
    /** @type {Record<string, HTMLElement | HTMLElement[]>} */
    const next = {};
    const elements = this.querySelectorAll(`[${REF_ATTR}]`);
    for (const el of elements) {
      // Don't capture refs that belong to a nested Component instance.
      if (this.#belongsToNestedComponent(el)) continue;
      const raw = el.getAttribute(REF_ATTR);
      if (!raw) continue;
      const isArray = raw.endsWith('[]');
      const name = isArray ? raw.slice(0, -2) : raw;
      if (!name) continue;
      if (isArray) {
        const arr = /** @type {HTMLElement[]} */ (next[name] || []);
        arr.push(/** @type {HTMLElement} */ (el));
        next[name] = arr;
      } else {
        next[name] = /** @type {HTMLElement} */ (el);
      }
    }
    this.refs = /** @type {Refs} */ (next);
  }

  #belongsToNestedComponent(el) {
    let cur = el.parentElement;
    while (cur && cur !== this) {
      if (cur instanceof Component) return true;
      cur = cur.parentElement;
    }
    return false;
  }

  #observeRefs() {
    this.#refObserver = new MutationObserver((mutations) => {
      let changed = false;
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === REF_ATTR) { changed = true; break; }
        if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) { changed = true; break; }
      }
      if (!changed) return;
      this.#hydrateRefs();
      this.updatedCallback();
    });
    this.#refObserver.observe(this, {
      attributes: true,
      attributeFilter: [REF_ATTR],
      childList: true,
      subtree: true,
    });
  }

  #validateRequiredRefs() {
    /** @type {string[]} */
    const required = /** @type {any} */ (this.constructor).requiredRefs || [];
    for (const name of required) {
      const ref = /** @type {any} */ (this.refs)[name];
      if (!ref || (Array.isArray(ref) && ref.length === 0)) {
        throw new MissingRefError(this.tagName.toLowerCase(), name);
      }
    }
  }
}

/**
 * Decorator-free helper: register a custom element only if not already registered. Use:
 *
 *   if (!customElements.get('cart-icon')) customElements.define('cart-icon', CartIcon);
 *
 * Or:
 *
 *   defineComponent('cart-icon', CartIcon);
 *
 * @param {string} name
 * @param {CustomElementConstructor} ctor
 * @param {ElementDefinitionOptions} [options]
 */
export function defineComponent(name, ctor, options) {
  if (!customElements.get(name)) customElements.define(name, ctor, options);
}

/**
 * DeclarativeShadowElement — opt-in subclass that adopts a declarative shadow root if present.
 * Use for components that ship server-rendered shadow DOM (`<template shadowrootmode="open">`).
 *
 * @template {Record<string, HTMLElement | HTMLElement[]>} [Refs=any]
 * @extends {Component<Refs>}
 */
export class DeclarativeShadowElement extends Component {
  connectedCallback() {
    super.connectedCallback();
    // Hydrate refs inside the shadow root too.
    if (this.shadowRoot) {
      // No-op: shadow refs aren't exposed via this.refs. Subclasses can read this.shadowRoot.querySelector('[ref="x"]') directly.
    }
  }
}

export default Component;
